/**
 * 런타임 구성 — 콘솔이 어느 API 를 부르는지.
 *
 * 값의 출처가 **빌드가 아니라 런타임**인 이유는 사내 배포 모델입니다: dev 에서 만든 이미지를
 * staging/prod 로 **재빌드 없이 promote** 하므로(공통 `_docker-promote.yaml`) 이미지는 스테이지에
 * 무관해야 합니다. 그래서 k8s ConfigMap 이 `/app/dist/config.js` 를 subPath 로 마운트하고,
 * `index.html` 이 앱 번들보다 **먼저** 그 파일을 로드해 전역 하나를 세웁니다:
 *
 *   window.__PLT_CONFIG__ = { apiBase: "https://api.dev.postdemy.com", loginHint: "postmath.co.kr" };
 *
 * 폴백 순서가 이 파일의 전부입니다 — **런타임(배포) → Vite env(로컬) → 빈 값(same-origin)**.
 * 가운데 단계를 남기는 이유는 로컬에서 `config.js` 가 404 이기 때문입니다(ConfigMap 은 클러스터에만
 * 있습니다). `.env.local` 의 `VITE_API_BASE` 로 `yarn dev` 만으로 개발할 수 있어야 합니다.
 */

declare global {
  interface Window {
    __PLT_CONFIG__?: { apiBase?: string; loginHint?: string };
  }
}

/** 기본 hd 테넌트 — 안내 문구에만 쓰이므로 미주입이어도 화면이 비지 않게 기본값을 둡니다. */
const DEFAULT_LOGIN_HINT = "postmath.co.kr";

/**
 * 구성은 모듈 로드 시점에 한 번 읽습니다.
 *
 * 함수로 매번 읽지 않는 이유: `config.js` 는 앱 번들보다 먼저 실행이 **보장**되므로(index.html 의
 * 첫 script, type=module 인 앱 번들은 defer 와 같은 타이밍) 이후에 값이 바뀔 일이 없습니다.
 * 값이 한 번 고정되면 "호출 시점에 따라 API base 가 다르다" 는 진단하기 어려운 분기도 사라집니다.
 */
function readConfig(): { apiBase: string; loginHint: string } {
  // SSR 이 아니므로 window 는 항상 있지만, 테스트 환경(node pool)에서의 방어를 위해 optional 로 읽습니다.
  const runtime = (typeof window === "undefined" ? undefined : window.__PLT_CONFIG__) ?? {};
  return {
    // 끝 슬래시를 떼는 이유는 호출부가 항상 `/auth/session` 처럼 선행 슬래시를 붙이기 때문입니다
    // (떼지 않으면 `https://api…//auth/session` 이 되고, 일부 게이트웨이가 그 경로를 404 로 봅니다).
    apiBase: (runtime.apiBase ?? import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, ""),
    loginHint: runtime.loginHint ?? import.meta.env.VITE_LOGIN_HINT ?? DEFAULT_LOGIN_HINT,
  };
}

const config = readConfig();

/**
 * API 접두 — 콘솔(`auth.<env>.postdemy.com`)과 API(`api.<env>.postdemy.com`)는 **다른 호스트**라
 * 배포 환경에서는 절대 URL 입니다. 빈 문자열은 콘솔과 API 가 같은 origin 인 로컬 구성에서만 유효합니다.
 *
 * 두 호스트는 같은 registrable domain(`postdemy.com`) 이라 **same-site** 이므로,
 * `SameSite=Lax` 세션 쿠키가 `credentials: "include"` fetch 에 실립니다.
 */
export const API_BASE = config.apiBase;

/** hd 강제 테넌트(로그인 안내 문구에만 쓰입니다). */
export const LOGIN_HINT = config.loginHint;

/** 콘솔은 호스트 루트에 마운트됩니다(경로 접두 없음). */
export const CONSOLE_BASE = "";

/** API 절대 경로 조립기. */
export const apiURL = (path: string): string => `${API_BASE}${path}`;
